import { useMutation, useQueryClient } from '@tanstack/react-query';
import QualAPI from '../../utils/QualAPI';
import TranscriptFormatter from '../../utils/TranscriptFormatter';
import { projectKeys } from '../projects/queries';
import { transcriptKeys } from '../transcripts/queries';
import { normalizeId } from '../utils/idNormalization';

/**
 * Generic mutation to create a transcript with body and name
 * @returns {Object} The mutation object
 */
export const useCreateTranscript = () => {
  const queryClient = useQueryClient();
  const transcriptFormatter = new TranscriptFormatter();

  return useMutation({
    mutationFn: async ({ projectId, name, body }) => {
      const result = await QualAPI.createTranscript(
        projectId,
        name,
        transcriptFormatter.run(body)
      );
      return result.data;
    },
    
    onSuccess: (data, variables) => {
      const { transcript } = data;

      if (transcript && variables.projectId) {
        const normalizedProjectId = normalizeId(variables.projectId);
        const normalizedTranscriptId = normalizeId(transcript.id);

        // Update the transcript list cache to include the new transcript immediately
        queryClient.setQueryData(
          transcriptKeys.list(normalizedProjectId),
          (oldData) => {
            if (!oldData) return oldData;

            // Check if transcript already exists (avoid duplicates)
            const exists = oldData?.some(t => t.id === transcript.id);
            if (exists) return oldData;

            return [...(oldData || []), transcript];
          }
        );

        // Invalidate the transcript count to ensure it's updated
        queryClient.invalidateQueries({
          queryKey: transcriptKeys.count(normalizedProjectId)
        });

        // Also update the transcript name cache
        queryClient.setQueryData(
          transcriptKeys.name(normalizedTranscriptId),
          transcript
        );
      }
    },
    
    onError: (error) => {
      console.error('Failed to create transcript:', error);
    }
  });
};